import { useState, useRef } from "react";
import { useLocation } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/theme-toggle";
import { Eye, EyeOff, Loader2, LogIn, KeyRound, ShieldAlert } from "lucide-react";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const getTurnstileToken = (): string | undefined => {
    return turnstileRef.current
      ?.querySelector<HTMLInputElement>('[name="cf-turnstile-response"]')
      ?.value || undefined;
  };

  const resetTurnstile = () => {
    if (window.turnstile && turnstileRef.current) {
      window.turnstile.reset(turnstileRef.current);
    }
  };

  const loginMutation = useMutation({
    mutationFn: async (data: LoginForm) => {
      const turnstileToken = requiresCaptcha ? getTurnstileToken() : undefined;
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...data,
          ...(turnstileToken ? { turnstileToken } : {}),
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        const err = new Error(json.message || "Login failed") as any;
        err.requiresCaptcha = json.requiresCaptcha;
        err.remainingAttempts = json.remainingAttempts;
        throw err;
      }
      return json;
    },
    onSuccess: (data) => {
      if (data.requiresTwoFactor) {
        setRequires2FA(true);
        toast({
          title: "Verification Required",
          description: "Please enter your authenticator code to continue.",
        });
      } else {
        queryClient.setQueryData(["/api/auth/user"], data.user);
        toast({
          title: "Welcome back",
          description: "You have successfully logged in.",
        });
        const isInternal = data.user?.roleScope === "internal" || 
          ["super_admin", "admin", "editor"].includes(data.user?.role || "");
        setLocation(isInternal ? "/admin" : "/");
      }
    },
    onError: (error: any) => {
      if (error?.requiresCaptcha && TURNSTILE_SITE_KEY) {
        setRequiresCaptcha(true);
      }
      resetTurnstile();
      toast({
        variant: "destructive",
        title: "Login failed",
        description: error.message || "Invalid email or password",
      });
    },
  });

  const verify2FAMutation = useMutation({
    mutationFn: async ({ token, trustDevice }: { token: string; trustDevice: boolean }) => {
      const response = await apiRequest("POST", "/api/auth/login/2fa-verify", { token, trustDevice });
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/auth/user"], data.user);
      toast({
        title: "Welcome back",
        description: data.usedBackupCode 
          ? "Logged in using backup code. Consider regenerating your backup codes."
          : "You have successfully logged in.",
      });
      const isInternal = data.user?.roleScope === "internal" || 
        ["super_admin", "admin", "editor"].includes(data.user?.role || "");
      setLocation(isInternal ? "/admin" : "/");
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Verification failed",
        description: error.message || "Invalid verification code",
      });
      setTwoFactorToken("");
    },
  });

  const onSubmit = (data: LoginForm) => {
    loginMutation.mutate(data);
  };

  const handleVerify2FA = () => {
    if (twoFactorToken.length >= 6) {
      verify2FAMutation.mutate({ token: twoFactorToken, trustDevice });
    }
  };

  if (requires2FA) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="absolute right-4 top-4">
          <ThemeToggle />
        </div>

        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <KeyRound className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">Two-Factor Authentication</CardTitle>
            <CardDescription>
              Enter the 6-digit code from your authenticator app, or an 8-character backup code.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center space-y-4">
              <InputOTP
                maxLength={8}
                value={twoFactorToken}
                onChange={setTwoFactorToken}
                data-testid="input-2fa-code"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                  <InputOTPSlot index={6} />
                  <InputOTPSlot index={7} />
                </InputOTPGroup>
              </InputOTP>
              <p className="text-xs text-muted-foreground">
                6 digits for authenticator code, 8 characters for backup code
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="trust-device"
                checked={trustDevice}
                onCheckedChange={(checked) => setTrustDevice(checked === true)}
                data-testid="checkbox-trust-device"
              />
              <label
                htmlFor="trust-device"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Trust this device for 30 days
              </label>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={handleVerify2FA}
                disabled={twoFactorToken.length < 6 || verify2FAMutation.isPending}
                className="w-full"
                data-testid="button-verify-2fa"
              >
                {verify2FAMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Verify
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setRequires2FA(false);
                  setTwoFactorToken("");
                }}
                data-testid="button-back-to-login"
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <LogIn className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Welcome Back</CardTitle>
          <CardDescription>
            Sign in to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        autoComplete="email"
                        data-testid="input-email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          autoComplete="current-password"
                          data-testid="input-password"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-1/2 -translate-y-1/2 no-default-hover-elevate no-default-active-elevate"
                          onClick={() => setShowPassword(!showPassword)}
                          data-testid="button-toggle-password"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {requiresCaptcha && TURNSTILE_SITE_KEY && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                    <ShieldAlert className="h-4 w-4 flex-shrink-0" />
                    <span>Please complete the security check below</span>
                  </div>
                  <div
                    ref={turnstileRef}
                    className="cf-turnstile"
                    data-sitekey={TURNSTILE_SITE_KEY}
                    data-theme="auto"
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loginMutation.isPending}
                data-testid="button-login"
              >
                {loginMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Sign In
              </Button>
            </form>
          </Form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link href="/register" className="font-medium text-primary hover:underline" data-testid="link-register">
              Register
            </Link>
          </div>

          <div className="mt-2 text-center">
            <Link href="/" className="text-sm text-muted-foreground hover:underline" data-testid="link-home">
              Back to Home
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
